import React from 'react';

import styled from 'styled-components';
import Skeleton from 'react-loading-skeleton';
import {
  CaptionText,
  FlexColumn,
  FlexRow,
  HashLength,
  useMatchMedia,
} from '@make-software/cspr-design';
import { BodyText } from '@block-explorer/ui';
import { CsprAmount } from '../cspr-amount/cspr-amount';
import { Trans, useTranslation } from 'react-i18next';
import Big from 'big.js';
import { PrecisionCase } from '../../utils/currency';
import { useGetAccountInfo } from '../../hooks/event-store-hooks';
import { AccountModel } from '@block-explorer/entities';
import { deriveAccountInfo } from '../../utils/account';
import AccountInfoDataWithBadge from '../account-info-data-with-badge/account-info-data-with-badge';

const StyledFlexRow = styled(FlexRow)<{ readOnly?: boolean }>(
  ({ theme, readOnly }) =>
    theme.withMedia({
      minHeight: 48,
      borderRadius: theme.borderRadius.base,
      padding: '8px 16px',
      backgroundColor: readOnly
        ? theme.styleguideColors.backgroundPrimary
        : theme.styleguideColors.fillSecondary,
      border: readOnly ? theme.border.base : 'unset',
    })
);

const StyledFlexColumn = styled(FlexColumn)<{ disabled?: boolean }>(
  ({ disabled }) => ({
    ...(disabled && {
      opacity: 0.5,
    }),
  })
);

const BalanceTextTitle = styled(CaptionText)(({ theme }) => ({
  textAlign: 'right',
}));

const BalanceText = styled(BodyText)(({ theme }) => ({
  textAlign: 'right',
}));

export interface AccountInfoRowWithCsprNameProps {
  publicKey: string;
  label: string;
  loading?: boolean;
  error?: string | null;
  accountEmpty?: boolean;
  disabled?: boolean;
  showBalance?: boolean;
  readOnly?: boolean;
}

export const AccountInfoInputWithCsprName = (
  props: Omit<AccountInfoRowWithCsprNameProps, 'label'>
) => {
  const { t } = useTranslation();
  const {
    publicKey,
    accountEmpty,
    showBalance = false,
    readOnly = true,
  } = props;

  const desktopHashSize = showBalance ? HashLength.SMALL : HashLength.LARGE;

  const responsiveHashSize = useMatchMedia(
    [HashLength.TINY, HashLength.LITTLE, desktopHashSize, desktopHashSize],
    []
  );

  const {
    data: accountInfoData,
    loading: accountInfoLoading,
    error: accountInfoError,
  } = useGetAccountInfo(
    publicKey
      ? {
          accountHash: AccountModel({
            publicKeyHex: publicKey,
          }).getAccountHash(),
          includes: 'account_info,centralized_account_info,cspr_name',
        }
      : null
  );
  const loading = accountInfoLoading || props.loading;
  const error = accountInfoError || props.error;

  const accountInfo = deriveAccountInfo(
    accountInfoData?.data?.account_info ||
      accountInfoData?.data.centralized_account_info
  );
  const logo = accountInfo?.logo;
  const name = accountInfo?.name;
  const csprName = accountInfoData?.data?.cspr_name;

  const emptyBalance =
    accountEmpty ||
    (!loading &&
      accountInfoData?.data?.balance != null &&
      Big(accountInfoData?.data?.balance).lte('0'));

  if (loading) {
    return (
      <StyledFlexRow justify="space-between" align="center" readOnly>
        <Skeleton />
      </StyledFlexRow>
    );
  }

  return (
    <StyledFlexRow
      justify="space-between"
      align="center"
      readOnly={readOnly || !publicKey}
    >
      {publicKey && (
        <>
          <AccountInfoDataWithBadge
            loading={false}
            logo={logo}
            name={name}
            publicKey={publicKey}
            csprName={csprName}
            hashLength={responsiveHashSize}
            isCopyEnabled
          />
          {showBalance ? (
            <FlexColumn>
              <BalanceTextTitle size={2} variation="darkGray">
                <Trans t={t}>Balance</Trans>
              </BalanceTextTitle>
              <BalanceText size={3} monotype>
                {emptyBalance ? (
                  <CsprAmount
                    motes={'0'}
                    precisionCase={PrecisionCase.deployCost}
                  />
                ) : loading ? (
                  t('Loading...')
                ) : error != null ? (
                  error
                ) : (
                  <CsprAmount
                    motes={accountInfoData?.data?.balance}
                    precisionCase={PrecisionCase.deployCost}
                  />
                )}
              </BalanceText>
            </FlexColumn>
          ) : null}
        </>
      )}
    </StyledFlexRow>
  );
};

export const AccountInfoRowWithCsprName = (
  props: AccountInfoRowWithCsprNameProps
) => (
  <StyledFlexColumn disabled={props.disabled} gap={4}>
    <FlexRow justify="space-between">
      <BodyText scale={'sm'} size={1}>
        {props.label}
      </BodyText>
    </FlexRow>
    <AccountInfoInputWithCsprName {...props} />
  </StyledFlexColumn>
);

export default AccountInfoRowWithCsprName;
